"use client";

import { Section } from "@/components/section";
import { Card, CardContent } from "@/components/ui/card";
import { Item, ItemContent, ItemTitle } from "@/components/ui/item";
import { Text } from "@/components/ui/text";
import { decodeHtmlEntities } from "@/helpers/strip-html-tags";
import { cn } from "@/lib/utils";
import { useMemo, useEffect, useState, useRef } from "react";

type CommonPageContentsProps = {
  section: PageSections;
  data: {
    description: string;
  };
};

// Use RegExp for lightweight HTML processing
function getProcessedContent(htmlString: string): {
  content: string;
  headings: { text: string; slug: string }[];
} {
  if (!htmlString) {
    return { content: "", headings: [] };
  }

  const headings: { text: string; slug: string }[] = [];

  const content = htmlString.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/gi, (match, attrs, innerHtml) => {
    const text = decodeHtmlEntities(innerHtml.replace(/<[^>]*>/g, "")).trim() || "heading";
    const slug = text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
    headings.push({ text, slug });

    let newAttrs = attrs;
    if (/id=/i.test(attrs)) {
      newAttrs = attrs.replace(/id=(['"])(.*?)\1/i, `id="${slug}"`);
    } else {
      newAttrs = ` id="${slug}"` + attrs;
    }

    return `<h2${newAttrs}>${innerHtml}</h2>`;
  });

  return {
    content,
    headings,
  };
}

const CommonPageContents = ({ data, section }: CommonPageContentsProps) => {
  const [activeHash, setActiveHash] = useState<string>("");
  const hasScrolledRef = useRef(false);
  const isScrollingToRef = useRef<string | null>(null);

  const { content, headings } = useMemo(
    () => getProcessedContent(data.description),
    [data.description]
  );

  const handleTocClick = (e: React.MouseEvent<HTMLAnchorElement>, slug: string) => {
    e.preventDefault();
    const element = document.getElementById(slug);
    if (element) {
      const newHash = `#${slug}`;
      setActiveHash(newHash);
      isScrollingToRef.current = newHash;

      const yOffset = -80; // offset to account for sticky header
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
      window.history.pushState(null, "", newHash);

      // Reset the ref after smooth scroll is expected to complete
      setTimeout(() => {
        isScrollingToRef.current = null;
      }, 800);
    }
  };

  // Scroll to initial hash on mount/load
  useEffect(() => {
    if (headings.length === 0 || hasScrolledRef.current) return;

    const initialHash = window.location.hash;
    if (initialHash) {
      const targetId = initialHash.replace("#", "");
      const element = document.getElementById(targetId);
      if (element) {
        hasScrolledRef.current = true;
        const timer = setTimeout(() => {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
          setActiveHash(initialHash);
        }, 150);
        return () => clearTimeout(timer);
      }
    }
  }, [headings]);

  // Sync scroll position with active hash
  useEffect(() => {
    if (headings.length === 0) return;

    let ticking = false;

    const handleScroll = () => {
      if (isScrollingToRef.current) {
        setActiveHash(isScrollingToRef.current);
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (isScrollingToRef.current) {
            setActiveHash(isScrollingToRef.current);
            ticking = false;
            return;
          }

          // Detect if we have scrolled to the bottom of the page
          const isAtBottom =
            window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50;

          if (isAtBottom) {
            setActiveHash(`#${headings[headings.length - 1].slug}`);
            ticking = false;
            return;
          }

          let currentActive = "";
          for (let i = 0; i < headings.length; i++) {
            const el = document.getElementById(headings[i].slug);
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            // 150px threshold to handle offset from top of viewport
            if (rect.top <= 150) {
              currentActive = `#${headings[i].slug}`;
            } else {
              break;
            }
          }

          if (currentActive) {
            setActiveHash(currentActive);
            if (window.location.hash !== currentActive) {
              window.history.replaceState(null, "", currentActive);
            }
          } else if (headings.length > 0) {
            setActiveHash(`#${headings[0].slug}`);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    // Run once to initialize
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [headings]);

  return (
    <Section id={section.sectionId} className={section.sectionClass}>
      <div className="grid grid-cols-12 gap-5">
        <div className="relative col-span-12 sm:col-span-3">
          <div className="sticky top-20">
            <Card className="gap-0 p-0">
              <CardContent className="p-2">
                <div className="flex w-full max-w-md flex-col gap-2">
                  {headings.map((h) => {
                    const isActive = activeHash === `#${h.slug}`;
                    return (
                      <Item
                        key={h.slug}
                        render={
                          <a
                            href={`#${h.slug}`}
                            onClick={(e) => handleTocClick(e, h.slug)}
                            className={cn(
                              "[a]:hover:bg-primary [a]:hover:text-primary-foreground transition-colors",
                              {
                                "bg-primary text-primary-foreground": isActive,
                              }
                            )}
                          >
                            <ItemContent>
                              <ItemTitle>{h.text}</ItemTitle>
                            </ItemContent>
                          </a>
                        }
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="relative col-span-12 sm:col-span-9">
          <Card className="p-4">
            <Text render={<div className="typeset typeset-docs [&_a]:text-primary" />}>
              {content}
            </Text>
          </Card>
        </div>
      </div>
    </Section>
  );
};

export default CommonPageContents;
